import { Component, OnInit, ChangeDetectorRef, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ClinicLocation, ClinicLocationService } from '../../services/clinic-location.service';
import { environment } from 'src/environments/environment';
import {
  setupMapboxGL,
  createMap,
  createNavigationControl,
  createPopup,
  createLngLatBounds
} from '../../components/patient-map/mapbox-gl-workaround';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

// Interface para os itens da lista de localizações
interface LocationListItem {
  id: string;
  name: string;
  count: number;
  visible: boolean;
  originalLocation: ClinicLocation;
}

@Component({
  selector: 'app-clinic-map',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './clinic-map.component.html',
  styleUrls: ['./clinic-map.component.scss']
})
export class ClinicMapComponent implements OnInit, OnDestroy {
  mapboxToken: string = environment.mapboxToken;
  map: any = null;
  style = 'mapbox://styles/mapbox/light-v10';
  
  // Coordenadas iniciais (Brasil)
  lat = -15.7801;
  lng = -47.9292;
  zoom = 4;

  // Armazenamento de localizações
  locations: ClinicLocation[] = [];
  visibleLocations: ClinicLocation[] = [];
  
  // Lista para o UI
  locationsList: LocationListItem[] = [];
  filteredLocationsList: LocationListItem[] = [];
  
  // Busca
  searchTerm: string = '';
  private searchSubject = new Subject<string>();
  
  // Estado da UI
  isLoading: boolean = true;
  error: string | null = null;
  selectedLocation: ClinicLocation | null = null;
  
  // Controle de responsividade
  isMobileView: boolean = false;
  showFilters: boolean = true;
  
  // Controle de clustering
  private clustersInitialized: boolean = false;
  
  // Controle de popup ativo
  private currentPopup: any = null;
  
  // Para limpeza de observables
  private destroy$ = new Subject<void>();

  constructor(
    private clinicLocationService: ClinicLocationService,
    private cdr: ChangeDetectorRef
  ) { 
    this.checkScreenSize();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.checkScreenSize();
  }
  
  checkScreenSize() {
    this.isMobileView = window.innerWidth < 768;
    if (this.isMobileView) {
      this.showFilters = false;
    } else {
      this.showFilters = true;
    }
  }
  
  toggleFiltersPanel() {
    this.showFilters = !this.showFilters;
    
    setTimeout(() => {
      if (this.map) {
        this.map.resize();
        
        if (this.visibleLocations.length > 0) {
          const bounds = createLngLatBounds();
          
          this.visibleLocations.forEach(location => {
            if (location.latitude && location.longitude) {
              bounds.extend([location.longitude, location.latitude]);
            }
          });
          
          if (!bounds.isEmpty()) {
            this.map.fitBounds(bounds, {
              padding: 50,
              duration: 500,
              maxZoom: 9
            });
          }
        }
      }
    }, 300);
  }

  ngOnInit() {
    this.searchSubject.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.filterLocations();
    });
    
    this.fetchLocations();
    
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        this.checkScreenSize();
        if (this.map) {
          this.map.resize();
          this.updateMapWithClustering();
        }
      }, 200);
    });
  }
  
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    
    this.closeCurrentPopup();
    
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  fetchLocations() {
    this.isLoading = true;
    this.error = null;
    
    
    
    this.clinicLocationService.getLocations().subscribe({
      next: (data) => {
        
        
        if (!data || !Array.isArray(data)) {
          this.error = 'Dados inválidos recebidos da API';
          this.isLoading = false;
          return;
        }

        this.locations = data.filter(location => 
          typeof location.latitude === 'number' && 
          typeof location.longitude === 'number'
        );

       

        if (this.locations.length === 0) {
          this.error = 'Nenhuma localização de clínica encontrada para exibir no mapa';
          this.isLoading = false;
          return;
        }

        this.prepareLocationsList();
        this.visibleLocations = [...this.locations];
        
        this.isLoading = false;
        
        setTimeout(() => {
          this.initializeMap();
          this.cdr.detectChanges();
        }, 100);
      },
      error: (error) => {
        console.error('Erro ao buscar localizações:', error);
        this.error = 'Erro ao carregar dados do mapa. Por favor, tente novamente.';
        this.isLoading = false;
      }
    });
  }
  
  prepareLocationsList() {
    const locationMap = new Map<string, LocationListItem>();
    
    this.locations.forEach(loc => {
      const locationId = this.getLocationId(loc);
      const locationName = loc.clinicName || this.getLocationName(loc);
      
      if (!locationMap.has(locationId)) {
        locationMap.set(locationId, {
          id: locationId,
          name: locationName,
          count: loc.clinicCount || 1,
          visible: true,
          originalLocation: loc
        });
      } else {
        const existing = locationMap.get(locationId)!;
        existing.count += loc.clinicCount || 1;
      }
    });
    
    this.locationsList = Array.from(locationMap.values())
      .sort((a, b) => a.name.localeCompare(b.name));
    
    this.filteredLocationsList = [...this.locationsList];
  }
  
  private getLocationId(location: ClinicLocation): string {
    if (location.cnpj) {
      return location.cnpj;
    }
    return `${location.city || 'unknown'}_${location.state || 'unknown'}`;
  }
  
  private getLocationName(location: ClinicLocation): string {
    if (location.clinicName) {
      return location.clinicName;
    }
    if (location.city && location.state) {
      return `${location.city}, ${location.state}`;
    } else if (location.city) {
      return location.city;
    } else if (location.state) {
      return location.state;
    } else if (location.country) {
      return `${location.country} (local desconhecido)`;
    }
    return 'Localização desconhecida';
  }

  filterLocations() {
    const term = this.searchTerm.trim().toLowerCase();
    
    if (!term) {
      this.filteredLocationsList = [...this.locationsList];
    } else {
      this.filteredLocationsList = this.locationsList.filter(loc => 
        loc.name.toLowerCase().includes(term)
      );
    }
  }
  
  updateVisibleLocations() {
    const visibleIds = new Set(
      this.locationsList
        .filter(loc => loc.visible)
        .map(loc => this.getLocationId(loc.originalLocation))
    );
    
    this.visibleLocations = this.locations.filter(loc => 
      visibleIds.has(this.getLocationId(loc))
    );
    
    this.updateMapWithClustering();
  }
  
  toggleAllLocations(allVisible: boolean) {
    this.locationsList.forEach(loc => loc.visible = allVisible);
    this.filteredLocationsList.forEach(loc => loc.visible = allVisible);
    this.updateVisibleLocations();
  }

  initializeMap() {
    if (this.map) {
      this.map.resize();
      this.updateMapWithClustering();
      return;
    }
    
    if (!this.mapboxToken) {
      this.error = 'Token do Mapbox não configurado';
      return;
    }
    
    try {
      setupMapboxGL(this.mapboxToken);
      
      this.map = createMap('clinic-map', {
        style: this.style,
        center: [this.lng, this.lat],
        zoom: this.zoom,
        minZoom: 3,
        maxZoom: 15,
        attributionControl: false,
        pitchWithRotate: false
      });
      
      this.map.addControl(
        createNavigationControl(), 
        'top-right'
      );
      
      this.map.on('load', () => {
        this.initializeClustering();
        this.updateMapWithClustering();
        
        const mapContainer = document.getElementById('clinic-map');
        if (mapContainer && mapContainer.clientHeight < 50) {
          console.warn('Contêiner do mapa tem altura muito pequena, forçando resize');
          mapContainer.style.height = '350px';
          setTimeout(() => {
            this.map.resize();
          }, 100);
        }
      });

      this.map.on('click', (e: any) => {
        const features = this.map.queryRenderedFeatures(e.point, {
          layers: ['clinic-clusters', 'unclustered-clinic-point']
        });
        
        if (features.length === 0) {
          this.closeCurrentPopup();
        }
      });
      
      if (this.isMobileView) {
        this.map.setMinZoom(4);
      }
    } catch (error) {
      console.error('Erro ao inicializar o mapa:', error);
      this.error = 'Não foi possível inicializar o mapa. Verifique a conexão e tente novamente.';
    }
  }
  
  private initializeClustering() {
    if (!this.map || this.clustersInitialized) return;

    this.map.addSource('clinics', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      },
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
      clusterProperties: {
        'sum_clinics': ['+', ['get', 'clinicCount']]
      }
    });

    // Camada para os clusters - cores em tons de verde/azul para diferenciar de pacientes
    this.map.addLayer({
      id: 'clinic-clusters',
      type: 'circle',
      source: 'clinics',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'sum_clinics'],
          '#fffaf8', // Orange 50 para <5
          5,
          '#ffcdb2', // Orange 200 para 5-10
          11,
          '#ffa06b', // Orange 300 para 11-20
          21,
          '#ff8948', // Orange 400 para 21-50
          51,
          '#d36f42', // Orange 500 para 51-100
          101,
          '#ab5e3c'  // Orange 600 para 100+
        ],
        'circle-radius': [
          'step',
          ['get', 'sum_clinics'],
          16, // <5 clínicas
          5, 20, // 5-10 clínicas
          11, 25, // 11-20 clínicas
          21, 30, // 21-50 clínicas
          51, 35, // 51-100 clínicas
          101, 40  // 100+ clínicas
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff'
      }
    });

    this.map.addLayer({
      id: 'clinic-cluster-count',
      type: 'symbol',
      source: 'clinics',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'sum_clinics'],
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': '#000000',
        'text-halo-width': 1
      }
    });

    this.map.addLayer({
      id: 'unclustered-clinic-point',
      type: 'circle',
      source: 'clinics',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': '#ff8948',
        'circle-radius': 14,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff'
      }
    });

    this.map.addLayer({
      id: 'unclustered-clinic-point-icon',
      type: 'symbol',
      source: 'clinics',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'text-field': '🏥',
        'text-size': 12
      }
    });

    // Eventos de clique nos clusters
    this.map.on('click', 'clinic-clusters', (e: any) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['clinic-clusters']
      });
      
      if (features.length > 0) {
        const feature = features[0];
        const coordinates = feature.geometry.coordinates.slice();
        const properties = feature.properties;
        
        this.openClusterPopup({
          latitude: coordinates[1],
          longitude: coordinates[0],
          totalClinics: properties.sum_clinics,
          pointCount: properties.point_count,
          clusterId: properties.cluster_id
        });
      }
    });

    this.map.on('click', 'unclustered-clinic-point', (e: any) => {
      const coordinates = e.features[0].geometry.coordinates.slice();
      const properties = e.features[0].properties;
      
      const location: ClinicLocation = {
        city: properties.city || '',
        state: properties.state || '',
        country: properties.country || '',
        latitude: coordinates[1],
        longitude: coordinates[0],
        clinicCount: properties.clinicCount,
        clinicName: properties.clinicName,
        cnpj: properties.cnpj
      };
      
      this.openPopup(location);
    });

    this.map.on('mouseenter', 'clinic-clusters', () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    
    this.map.on('mouseleave', 'clinic-clusters', () => {
      this.map.getCanvas().style.cursor = '';
    });
    
    this.map.on('mouseenter', 'unclustered-clinic-point', () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    
    this.map.on('mouseleave', 'unclustered-clinic-point', () => {
      this.map.getCanvas().style.cursor = '';
    });

    this.clustersInitialized = true;
  }
  
  private updateMapWithClustering() {
    if (!this.map || !this.clustersInitialized) return;
    
    const geoJsonData = this.createGeoJsonFromLocations(this.visibleLocations);
    
    const source = this.map.getSource('clinics');
    if (source) {
      source.setData(geoJsonData);
    }
    
    // Ajustar limites do mapa
    if (this.visibleLocations.length > 0) {
      const bounds = createLngLatBounds();
      
      this.visibleLocations.forEach(location => {
        if (location.latitude && location.longitude) {
          bounds.extend([location.longitude, location.latitude]);
        }
      });
      
      if (!bounds.isEmpty()) {
        this.map.fitBounds(bounds, {
          padding: 50,
          duration: 500,
          maxZoom: 9
        });
      }
    }
  }
  
  private createGeoJsonFromLocations(locations: ClinicLocation[]): any {
    return {
      type: 'FeatureCollection',
      features: locations.map(location => ({
        type: 'Feature',
        properties: {
          city: location.city || '',
          state: location.state || '',
          country: location.country || '',
          clinicCount: location.clinicCount || 1,
          clinicName: location.clinicName || '',
          cnpj: location.cnpj || ''
        },
        geometry: {
          type: 'Point',
          coordinates: [location.longitude, location.latitude]
        }
      }))
    };
  }
  
  private closeCurrentPopup() {
    if (this.currentPopup) {
      this.currentPopup.remove();
      this.currentPopup = null;
    }
    this.selectedLocation = null;
  }
  
  private openPopup(location: ClinicLocation) {
    this.closeCurrentPopup();
    
    const popupContent = `
      <div style="padding: 12px; min-width: 200px;">
        <h4 style="margin: 0 0 8px 0; color: #0288D1; font-size: 14px;">🏥 ${location.clinicName || 'Clínica'}</h4>
        ${location.cnpj ? `<p style="margin: 4px 0; font-size: 12px; color: #666;">CNPJ: ${location.cnpj}</p>` : ''}
        <p style="margin: 4px 0; font-size: 12px; color: #666;">
          📍 ${location.city || 'N/A'}${location.state ? ', ' + location.state : ''}
        </p>
        ${location.country ? `<p style="margin: 4px 0; font-size: 12px; color: #666;">🌍 ${location.country}</p>` : ''}
      </div>
    `;
    
    this.currentPopup = createPopup({
      closeButton: true,
      closeOnClick: false
    })
      .setLngLat([location.longitude, location.latitude])
      .setHTML(popupContent)
      .addTo(this.map);
    
    this.selectedLocation = location;
  }
  
  private openClusterPopup(data: any) {
    this.closeCurrentPopup();
    
    const popupContent = `
      <div style="padding: 12px; min-width: 180px;">
        <h4 style="margin: 0 0 8px 0; color: #0288D1; font-size: 14px;">📊 Grupo de Clínicas</h4>
        <p style="margin: 4px 0; font-size: 13px;">
          <strong>${data.totalClinics}</strong> clínica${data.totalClinics > 1 ? 's' : ''} nesta região
        </p>
        <p style="margin: 8px 0 0 0; font-size: 11px; color: #666;">
          Aproxime o zoom para ver individualmente
        </p>
      </div>
    `;
    
    this.currentPopup = createPopup({
      closeButton: true,
      closeOnClick: false
    })
      .setLngLat([data.longitude, data.latitude])
      .setHTML(popupContent)
      .addTo(this.map);
  }
  
  recarregarDados() {
    this.fetchLocations();
  }
}
